// frontend/src/stores/company.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from './auth'

export const useCompanyStore = defineStore('company', () => {
  const services = ref([])
  const categories = ref([])
  const masters = ref([])
  const clients = ref([])
  const companyInfo = ref(null)
  const isLoading = ref(false)
  const lastLoaded = ref(null)
  const currentTariff = ref(null)
  const tariffLoading = ref(false)
  const schedules = ref([])
  const schedulesLoading = ref(false)
  const exceptions = ref([])

  // === profile — алиас для companyInfo (для совместимости с PrivacyEditor) ===
  const profile = computed(() => companyInfo.value)

  const isLoaded = computed(() => !!lastLoaded.value)

  // ========== WAITLIST SETTINGS ==========
  const waitlistSettings = ref({
    enabled: false,
    max_entries: 50,
    max_days_ahead: 30,
    min_hours_notice: 2,
    notify_client: 'both',
    notify_admin_new: true,
    notify_admin_expire: false,
    offer_timeout_minutes: 30,
    offer_mode: 'auto_fifo',
    priority_mode: 'fifo',
    show_position: true,
    auto_expire_hours: 72,
    allow_any_master: true,
    allow_all_services: true
  })

  async function loadSchedules() {
    if (!masters.value || masters.value.length === 0) {
      schedules.value = []
      return
    }
    schedulesLoading.value = true
    try {
      const allSchedules = await Promise.all(
        masters.value.map(async (m) => {
          try {
            const data = await getSchedules(m.id)
            return (data || []).map(s => ({ ...s, user_id: m.id }))
          } catch (err) {
            return []
          }
        })
      )
      schedules.value = allSchedules.flat()
    } catch (err) {
      console.error('Ошибка загрузки расписаний:', err)
      schedules.value = []
    } finally {
      schedulesLoading.value = false
    }
  }

  async function loadAll() {
    if (isLoading.value) return
    isLoading.value = true
    try {
      const [sRes, mRes, cRes, clRes] = await Promise.all([
        api.get('/services'),
        api.get('/masters'),
        api.get('/company'),
        api.get('/clients')
      ])
      services.value = sRes.data || []
      masters.value = mRes.data || []
      companyInfo.value = cRes.data || null
      clients.value = clRes.data?.clients || []
      loadCategories() // не блокируем загрузку, ошибки гасятся внутри
      lastLoaded.value = Date.now()

      await loadSchedules()

      if (companyInfo.value && !companyInfo.value.booking_mode) {
        companyInfo.value.booking_mode = 'sequential'
      }

      if (companyInfo.value) {
        waitlistSettings.value = {
          enabled: companyInfo.value.waitlist_enabled ?? false,
          max_entries: companyInfo.value.waitlist_max_entries ?? 50,
          max_days_ahead: companyInfo.value.waitlist_max_days_ahead ?? 30,
          min_hours_notice: companyInfo.value.waitlist_min_hours_notice ?? 2,
          notify_client: companyInfo.value.waitlist_notify_client ?? 'both',
          notify_admin_new: companyInfo.value.waitlist_notify_admin_new ?? true,
          notify_admin_expire: companyInfo.value.waitlist_notify_admin_expire ?? false,
          offer_timeout_minutes: companyInfo.value.waitlist_offer_timeout_minutes ?? 30,
          offer_mode: companyInfo.value.waitlist_offer_mode ?? 'auto_fifo',
          priority_mode: companyInfo.value.waitlist_priority_mode ?? 'fifo',
          show_position: companyInfo.value.waitlist_show_position ?? true,
          auto_expire_hours: companyInfo.value.waitlist_auto_expire_hours ?? 72,
          allow_any_master: companyInfo.value.waitlist_allow_any_master ?? true,
          allow_all_services: companyInfo.value.waitlist_allow_all_services ?? true
        }
      }
    } catch (err) {
      console.error('Ошибка загрузки данных компании:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function loadServices() {
    const { data } = await api.get('/services')
    services.value = data || []
  }

  async function loadMasters() {
    const { data } = await api.get('/masters')
    masters.value = data || []
  }

  async function loadClients() {
    // Загружаем всех клиентов для поиска в BookingForm
    const { data } = await api.get('/clients?limit=1000')
    clients.value = data.clients || []
  }

  async function createClient(payload) {
    const { data } = await api.post('/clients', payload)
    await loadClients()
    return data
  }

  async function createService(payload) {
    const { data } = await api.post('/services', payload)
    await loadServices()
    return data
  }

  async function updateService(id, payload) {
    const { data } = await api.put(`/services/${id}`, payload)
    await loadServices()
    return data
  }

  async function deleteService(id) {
    await api.delete(`/services/${id}`)
    await loadServices()
  }

  async function updateServicesOrder(updates) {
    await api.put('/services/order', { services: updates })
    await loadServices()
  }

  // ========== КАТЕГОРИИ УСЛУГ ==========
  async function loadCategories() {
    try {
      const { data } = await api.get('/service-categories')
      categories.value = data || []
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
      categories.value = []
    }
  }

  async function createCategory(payload) {
    const { data } = await api.post('/service-categories', payload)
    await loadCategories()
    return data
  }

  async function updateCategory(id, payload) {
    const { data } = await api.put(`/service-categories/${id}`, payload)
    await loadCategories()
    return data
  }

  async function deleteCategory(id) {
    await api.delete(`/service-categories/${id}`)
    await Promise.all([loadCategories(), loadServices()])
  }

  // ========== MASTERS (с is_active) ==========
  async function createMaster(payload) {
    const { data } = await api.post('/masters', payload)
    await loadMasters()
    return data
  }

  async function updateMaster(id, payload) {
    const { data } = await api.put(`/masters/${id}`, payload)
    const idx = masters.value.findIndex(m => m.id === id)
    if (idx !== -1) {
      masters.value[idx] = { ...masters.value[idx], ...data }
    }
    return data
  }

  async function deleteMaster(id) {
    await api.delete(`/masters/${id}`)
    await loadMasters()
  }

  async function getMastersByService(serviceId) {
    const { data } = await api.get(`/services/${serviceId}/masters`)
    return data || []
  }

  async function getMasterServices(masterId) {
    const { data } = await api.get(`/masters/${masterId}/services`)
    return data || []
  }

  async function getMasterServicesAll(masterId) {
    const { data } = await api.get(`/masters/${masterId}/services/all`)
    return data || []
  }

  async function saveMasterServices(masterId, serviceIds) {
    await api.post(`/masters/${masterId}/services`, { service_ids: serviceIds })
  }

  // Исключения графика (выходные, отпуска, особые часы) — подгружаем диапазон и сливаем по id
  async function loadExceptions(from, to) {
    try {
      const { data } = await api.get('/schedule-exceptions', { params: { from, to } })
      const rows = Array.isArray(data) ? data : (data?.exceptions || [])
      const keep = exceptions.value.filter(e => e.date < from || e.date > to)
      exceptions.value = [...keep, ...rows]
    } catch (err) {
      // Эндпоинт может отсутствовать/быть недоступен на тарифе или для роли — это не ошибка для пользователя
      if (err?.response?.status !== 404 && err?.response?.status !== 403) {
        console.warn('Не удалось загрузить исключения графика:', err?.message)
      }
    }
  }

  async function getSchedules(userId) {
    const { data } = await api.get(`/schedules/${userId}`)
    return data || []
  }

  async function updateSchedule(userId, payload) {
    await api.post(`/schedules/${userId}`, payload)
  }

  async function addBreak(userId, payload) {
    const { data } = await api.post(`/schedules/${userId}/breaks`, payload)
    return data
  }

  async function getBreaksByDay(userId, dayOfWeek) {
    const { data } = await api.get(`/schedules/${userId}/breaks/${dayOfWeek}`)
    return data || []
  }

  async function getBreaks(userId) {
    const { data } = await api.get(`/schedules/${userId}/breaks`)
    return data || []
  }

  async function saveBreak(userId, payload) {
    await api.post(`/schedules/${userId}/breaks`, payload)
  }

  async function deleteBreak(breakId) {
    await api.delete(`/schedules/breaks/${breakId}`)
  }

  async function getSlots(masterId, date, serviceIds = null, serviceDuration = null, bookingMode = null, services = null) {
    let url = `/slots?date=${date}`
    if (masterId) {
      url += `&user_id=${masterId}`
    } else if (serviceIds && serviceIds.length > 0) {
      url += `&service_ids=${serviceIds.join(',')}`
    }
    if (serviceDuration) {
      url += `&service_duration=${serviceDuration}`
    }
    if (bookingMode) {
      url += `&booking_mode=${bookingMode}`
    }
    if (services && services.length > 0) {
      url += `&services=${encodeURIComponent(JSON.stringify(services))}`
    }
    const { data } = await api.get(url)
    return data || []
  }

  async function getAvailableDates(masterId, month, serviceIds = null, serviceDuration = null, bookingMode = null, services = null) {
    let url = `/slots/dates?month=${month}`
    if (masterId) {
      url += `&user_id=${masterId}`
    } else if (serviceIds && serviceIds.length > 0) {
      url += `&service_ids=${serviceIds.join(',')}`
    }
    if (serviceDuration) {
      url += `&service_duration=${serviceDuration}`
    }
    if (bookingMode) {
      url += `&booking_mode=${bookingMode}`
    }
    if (services && services.length > 0) {
      url += `&services=${encodeURIComponent(JSON.stringify(services))}`
    }
    const { data } = await api.get(url)
    return data || []
  }

  async function getCompany() {
    const { data } = await api.get('/company')
    companyInfo.value = data || null

    if (companyInfo.value) {
      waitlistSettings.value = {
        enabled: companyInfo.value.waitlist_enabled ?? false,
        max_entries: companyInfo.value.waitlist_max_entries ?? 50,
        max_days_ahead: companyInfo.value.waitlist_max_days_ahead ?? 30,
        min_hours_notice: companyInfo.value.waitlist_min_hours_notice ?? 2,
        notify_client: companyInfo.value.waitlist_notify_client ?? 'both',
        notify_admin_new: companyInfo.value.waitlist_notify_admin_new ?? true,
        notify_admin_expire: companyInfo.value.waitlist_notify_admin_expire ?? false,
        offer_timeout_minutes: companyInfo.value.waitlist_offer_timeout_minutes ?? 30,
        offer_mode: companyInfo.value.waitlist_offer_mode ?? 'auto_fifo',
        priority_mode: companyInfo.value.waitlist_priority_mode ?? 'fifo',
        show_position: companyInfo.value.waitlist_show_position ?? true,
        auto_expire_hours: companyInfo.value.waitlist_auto_expire_hours ?? 72,
        allow_any_master: companyInfo.value.waitlist_allow_any_master ?? true,
        allow_all_services: companyInfo.value.waitlist_allow_all_services ?? true
      }
    }

    return data
  }

  async function loadProfile() {
    return getCompany()
  }

  async function updateCompany(payload) {
    const { data } = await api.put('/company', payload)
    companyInfo.value = data || null

    if (companyInfo.value) {
      waitlistSettings.value = {
        enabled: companyInfo.value.waitlist_enabled ?? waitlistSettings.value.enabled,
        max_entries: companyInfo.value.waitlist_max_entries ?? waitlistSettings.value.max_entries,
        max_days_ahead: companyInfo.value.waitlist_max_days_ahead ?? waitlistSettings.value.max_days_ahead,
        min_hours_notice: companyInfo.value.waitlist_min_hours_notice ?? waitlistSettings.value.min_hours_notice,
        notify_client: companyInfo.value.waitlist_notify_client ?? waitlistSettings.value.notify_client,
        notify_admin_new: companyInfo.value.waitlist_notify_admin_new ?? waitlistSettings.value.notify_admin_new,
        notify_admin_expire: companyInfo.value.waitlist_notify_admin_expire ?? waitlistSettings.value.notify_admin_expire,
        offer_timeout_minutes: companyInfo.value.waitlist_offer_timeout_minutes ?? waitlistSettings.value.offer_timeout_minutes,
        offer_mode: companyInfo.value.waitlist_offer_mode ?? waitlistSettings.value.offer_mode,
        priority_mode: companyInfo.value.waitlist_priority_mode ?? waitlistSettings.value.priority_mode,
        show_position: companyInfo.value.waitlist_show_position ?? waitlistSettings.value.show_position,
        auto_expire_hours: companyInfo.value.waitlist_auto_expire_hours ?? waitlistSettings.value.auto_expire_hours,
        allow_any_master: companyInfo.value.waitlist_allow_any_master ?? waitlistSettings.value.allow_any_master,
        allow_all_services: companyInfo.value.waitlist_allow_all_services ?? waitlistSettings.value.allow_all_services
      }
    }
    if (companyInfo.value && !companyInfo.value.booking_mode) {
      companyInfo.value.booking_mode = 'sequential'
    }
    return data
  }

  async function getUserProfile() {
    const { data } = await api.get('/users/me')
    return data
  }

  async function updateUserProfile(payload) {
    const { data } = await api.put('/users/me', payload)
    return data
  }

  async function loadCurrentTariff() {
    if (tariffLoading.value) return
    tariffLoading.value = true
    try {
      const { data } = await api.get('/payments/current-tariff')
      currentTariff.value = data || null
      return data
    } catch (err) {
      console.error('Ошибка загрузки тарифа:', err)
      currentTariff.value = null
      return null
    } finally {
      tariffLoading.value = false
    }
  }

  function $reset() {
    services.value = []
    categories.value = []
    masters.value = []
    clients.value = []
    companyInfo.value = null
    lastLoaded.value = null
    currentTariff.value = null 
    tariffLoading.value = false
    schedules.value = []
    schedulesLoading.value = false
    exceptions.value = []
    waitlistSettings.value = {
      enabled: false,
      max_entries: 50,
      max_days_ahead: 30,
      min_hours_notice: 2,
      notify_client: 'both',
      notify_admin_new: true,
      notify_admin_expire: false,
      offer_timeout_minutes: 30,
      offer_mode: 'auto_fifo',
      priority_mode: 'fifo',
      show_position: true,
      auto_expire_hours: 72,
      allow_any_master: true,
      allow_all_services: true
    }
  }

  return {
    services, categories, masters, clients, companyInfo, profile, isLoading, isLoaded,
    schedules, schedulesLoading, exceptions, loadExceptions,
    waitlistSettings,
    loadAll, loadServices, loadMasters, loadClients, createClient,
    createService, updateService, deleteService, updateServicesOrder,
    loadCategories, createCategory, updateCategory, deleteCategory,
    createMaster, updateMaster, deleteMaster,
    getMastersByService, getMasterServices, saveMasterServices,
    getSchedules, updateSchedule, getBreaks, getBreaksByDay, addBreak, saveBreak, deleteBreak,
    loadSchedules,
    getSlots, getAvailableDates,
    getCompany, loadProfile, updateCompany, getUserProfile, updateUserProfile, currentTariff, tariffLoading, loadCurrentTariff,
    $reset
  }
})